import { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
// components
import LoadingScreen from '../components/loading-screen';
//
import { PATH_AUTH, PATH_DASHBOARD } from 'src/routes/paths';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { useAuthContext } from './useAuthContext';
import { useUserContext } from './useUserContext';
// ----------------------------------------------------------------------

type AuthGuardProps = {
  children: React.ReactNode;
  /** Off for pages any verified account may use, such as presenting a
   *  webinar: terms and pending deletion still apply, clinic setup does not. */
  requireOnboarding?: boolean;
};

export default function AuthGuard({
  children,
  requireOnboarding = true,
}: AuthGuardProps) {
  const { isAuthenticated, isInitialized, loginWithCustomToken } =
    useAuthContext();
  const {
    isLoading,
    isProfessionalRole,
    isUserRole,
    data,
    isOrganizationAdminRole,
    isOrganizationOwnerRole,
  } = useUserContext();
  const { pathname, search } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const redirect = searchParams.get('redirect');
  const token = searchParams.get('token');
  const orderId = searchParams.get('orderId');
  const referer = searchParams.get('referer');
  const queryParams = {
    orderId,
    referer,
  };
  const {
    organizations,
    isOrganizationNotVerified,
    isLoading: isOrganizationsLoading,
    isError: isOrganizationsError,
  } = useOrganizationContext();

  const filteredParams = Object.fromEntries(
    Object.entries(queryParams).filter(([key, value]) => value != null),
  ) as Record<string, string>;
  const params = new URLSearchParams(filteredParams).toString();
  const redirectUrl = redirect
    ? `${params ? `${redirect}?${params}` : redirect}`
    : undefined;

  // Only gate redirects on the org query for roles that actually depend on it.
  // Plain `user` accounts disable the org query — and react-query v4 reports
  // `isLoading: true` on disabled queries, which would otherwise wedge them.
  const needsOrganizationData =
    isProfessionalRole || isOrganizationAdminRole || isOrganizationOwnerRole;
  const isOrgDataPending = needsOrganizationData && isOrganizationsLoading;
  const isOrgDataError = needsOrganizationData && isOrganizationsError;
  const isStaffWithoutOrganizations =
    needsOrganizationData && organizations.length === 0;

  useEffect(() => {
    const autoLoginAndRedirect = async () => {
      try {
        if (token) {
          const user = await loginWithCustomToken(token);

          if (!user) {
            navigate(PATH_AUTH.login, { replace: true });
          }
        }
      } catch (error) {
        navigate(PATH_AUTH.login, { replace: true });
      }
    };

    autoLoginAndRedirect();
  }, [loginWithCustomToken, token, navigate]);

  useEffect(() => {
    if (
      isInitialized &&
      !isLoading &&
      data?.pendingDeletion &&
      pathname !== PATH_AUTH.pendingDeletion
    ) {
      navigate(PATH_AUTH.pendingDeletion, { replace: true });
    }
  }, [isInitialized, isLoading, data?.pendingDeletion, pathname, navigate]);

  // Check for T&C acceptance - redirect to accept-terms page if needed
  useEffect(() => {
    if (
      isInitialized &&
      !isLoading &&
      !data?.pendingDeletion &&
      data?.requiresTermsAcceptance &&
      pathname !== PATH_AUTH.acceptTerms &&
      pathname !== PATH_AUTH.accountClosed
    ) {
      // The dashboard would detour a presenter through clinic onboarding.
      navigate(
        requireOnboarding
          ? PATH_AUTH.acceptTerms
          : `${PATH_AUTH.acceptTerms}?redirect=${encodeURIComponent(pathname)}`,
        { replace: true },
      );
    }
  }, [requireOnboarding, isInitialized, isLoading, data?.pendingDeletion, data?.requiresTermsAcceptance, pathname, navigate]);

  useEffect(() => {
    if (
      requireOnboarding &&
      isInitialized &&
      !isLoading &&
      data?.onboarded === false &&
      !data?.requiresTermsAcceptance &&
      // Already in the wizard, including the invitation step on its own route.
      !pathname.startsWith(PATH_DASHBOARD.general.updateAccount)
    ) {
      if (isProfessionalRole) {
        navigate(
          search
            ? `${PATH_DASHBOARD.general.updateAccount}${search}`
            : PATH_DASHBOARD.general.updateAccount,
        );
      } else if (isOrganizationAdminRole) {
        navigate(
          search
            ? `${PATH_DASHBOARD.general.updateAccount}${search}`
            : PATH_DASHBOARD.general.updateAccount,
        );
      } else if (isOrganizationOwnerRole) {
        navigate(
          search
            ? `${PATH_DASHBOARD.general.updateAccount}${search}`
            : PATH_DASHBOARD.general.updateAccount,
        );
      } else if (isUserRole) {
        navigate(
          search
            ? `${PATH_DASHBOARD.general.updateAccount}${search}`
            : PATH_DASHBOARD.general.updateAccount,
        );
      }
    }
  }, [
    requireOnboarding,
    isInitialized,
    isLoading,
    navigate,
    data?.onboarded,
    data?.requiresTermsAcceptance,
    search,
    isProfessionalRole,
    isOrganizationAdminRole,
    isOrganizationOwnerRole,
    isUserRole,
  ]);

  useEffect(() => {
    if (
      isInitialized &&
      !isLoading &&
      !isOrgDataPending &&
      !isOrgDataError &&
      !data?.pendingDeletion &&
      !data?.requiresTermsAcceptance &&
      (pathname === PATH_DASHBOARD.root ||
        pathname === PATH_DASHBOARD.general.updateAccount ||
        (isStaffWithoutOrganizations &&
          [
            PATH_DASHBOARD.general.calendar,
            PATH_DASHBOARD.general.waitingRoom,
            PATH_DASHBOARD.general.organizationVerification,
          ].includes(pathname))) &&
      data?.onboarded
    ) {
      if (isStaffWithoutOrganizations) {
        navigate(PATH_DASHBOARD.general.notifications.invites, { replace: true });
        return;
      }

      isUserRole &&
        navigate(redirectUrl || PATH_DASHBOARD.general.profiles.root);
      isProfessionalRole &&
        navigate(
          isOrganizationNotVerified
            ? PATH_DASHBOARD.general.organizationVerification
            : redirectUrl || PATH_DASHBOARD.general.waitingRoom,
        );
      isOrganizationAdminRole &&
        navigate(
          isOrganizationNotVerified
            ? PATH_DASHBOARD.general.organizationVerification
            : redirectUrl || PATH_DASHBOARD.general.calendar,
        );
      isOrganizationOwnerRole &&
        !isOrganizationAdminRole &&
        navigate(
          isOrganizationNotVerified
            ? PATH_DASHBOARD.general.organizationVerification
            : redirectUrl || PATH_DASHBOARD.general.calendar,
        );
      // Platform administration lives in the admin app.
      !isUserRole &&
        !needsOrganizationData &&
        navigate(redirectUrl || PATH_DASHBOARD.page401);
    }
  }, [
    isProfessionalRole,
    isUserRole,
    isOrganizationAdminRole,
    isOrganizationOwnerRole,
    navigate,
    isInitialized,
    isLoading,
    isOrgDataPending,
    isOrgDataError,
    pathname,
    data?.onboarded,
    data?.pendingDeletion,
    data?.requiresTermsAcceptance,
    redirectUrl,
    isStaffWithoutOrganizations,
    needsOrganizationData,
    isOrganizationNotVerified,
  ]);

  useEffect(() => {
    if (
      !requireOnboarding ||
      isStaffWithoutOrganizations ||
      isOrgDataError ||
      data?.pendingDeletion ||
      data?.requiresTermsAcceptance
    ) {
      return;
    }

    if (
      isInitialized &&
      !isLoading &&
      !isOrgDataPending &&
      data?.onboarded &&
      isOrganizationNotVerified &&
      (isProfessionalRole ||
        isOrganizationAdminRole ||
        isOrganizationOwnerRole) &&
      pathname !== PATH_DASHBOARD.general.organizationVerification &&
      pathname !== PATH_DASHBOARD.general.notifications.invites &&
      pathname !== PATH_DASHBOARD.general.notifications.root
    ) {
      navigate(PATH_DASHBOARD.general.organizationVerification, {
        replace: true,
      });
    }

    if (
      isInitialized &&
      !isLoading &&
      !isOrgDataPending &&
      data?.onboarded &&
      !isOrganizationNotVerified &&
      pathname === PATH_DASHBOARD.general.organizationVerification
    ) {
      if (isProfessionalRole) {
        navigate(PATH_DASHBOARD.general.waitingRoom, { replace: true });
      } else if (isOrganizationAdminRole || isOrganizationOwnerRole) {
        navigate(PATH_DASHBOARD.general.calendar, { replace: true });
      } else {
        navigate(PATH_DASHBOARD.root, { replace: true });
      }
    }
  }, [
    requireOnboarding,
    isInitialized,
    isLoading,
    isOrgDataPending,
    isOrgDataError,
    isStaffWithoutOrganizations,
    data?.onboarded,
    data?.pendingDeletion,
    data?.requiresTermsAcceptance,
    isOrganizationNotVerified,
    isProfessionalRole,
    isOrganizationAdminRole,
    isOrganizationOwnerRole,
    pathname,
    navigate,
  ]);

  useEffect(() => {
    if (!isAuthenticated && isInitialized && !token) {
      // Preserve where the (now logged-out) user was heading so the auth flow
      // can send them there after a successful login. Same `redirect` query
      // param the invitation links use — GuestGuard carries it over to
      // /dashboard, and the redirectUrl logic above navigates to it.
      const intendedPath = `${pathname}${search}`;
      navigate(
        intendedPath && intendedPath !== PATH_DASHBOARD.root
          ? `${PATH_AUTH.login}?redirect=${encodeURIComponent(intendedPath)}`
          : PATH_AUTH.login,
        { replace: true },
      );
    }
  }, [isAuthenticated, navigate, isInitialized, pathname, search, token]);

  if (!isInitialized || isLoading || !data) {
    return <LoadingScreen />;
  }

  return <> {children} </>;
}
